import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { theme } from '@frontend/theme';
import { Modal, ModalControlResponse, Text, Tray, useModalControl } from '@frontend/design-system';
import { useOverridesManagementPermission } from '../../userManagementPermission';
import { EmptyOverrideSetting, SettingContent } from '../../views/settings/overrides';
import { NewOverrideSettingRemoveModal } from '../override/override-settings-remove-modal.new';
import { PhoneOverrideType } from '../override/types';
import { CannotCreateOverrideModal } from './cannot-create-override-modal';
import { ChangeLocationModal } from './change-location-modal';
import { ExtendedCallRoute } from './types';

type Props = {
  callRoute: ExtendedCallRoute;
  locationId: string;
  editOverrideTray: ModalControlResponse;
};

export const OverridePanel = ({ callRoute, locationId, editOverrideTray }: Props) => {
  const { t } = useTranslation('phone');
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const phoneTenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const removeOverride = useModalControl();
  const cannotCreateOverrideModal = useModalControl();
  const changeLocationForOverrideModal = useModalControl();
  const override = callRoute.callRoutePhoneOverride;
  const hasUserManagementPermission = useOverridesManagementPermission();

  const gatedOverrideTray: ModalControlResponse = callRoute.locationId
    ? editOverrideTray
    : {
        ...editOverrideTray,
        // Override the openModal function to conditionally open the cannotCreateOverrideModal if there is no location
        // assigned to the Call Route, otherwise open the editOverrideTray as normal
        openModal: () => cannotCreateOverrideModal.openModal(),
      };

  return (
    <>
      <Tray.Body>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Text size='medium'>
            {t(
              'Overrides enable you to quickly and easily reroute your calls to secure phone coverage during breaks and holidays.'
            )}{' '}
          </Text>
          {override && override.id ? (
            <SettingContent
              override={override}
              isSideModal={true}
              editOverride={editOverrideTray}
              removeOverride={removeOverride}
              hasUserManagementPermission={hasUserManagementPermission(override?.locationId ?? '')}
            />
          ) : (
            <EmptyOverrideSetting
              locationId={locationId}
              override={override}
              isSideModal={true}
              editOverride={gatedOverrideTray}
              hasUserManagementPermission={hasUserManagementPermission(override?.locationId ?? '')}
            />
          )}
        </div>
      </Tray.Body>
      <NewOverrideSettingRemoveModal
        override={override ?? ({} as PhoneOverrideType)}
        modalProps={removeOverride.modalProps}
        phoneTenantId={phoneTenantId}
      />

      <Modal {...cannotCreateOverrideModal.modalProps} minWidth={580}>
        <CannotCreateOverrideModal
          callRouteName={callRoute.name}
          onClose={cannotCreateOverrideModal.modalProps.onClose}
          onAssignLocationClick={() => {
            cannotCreateOverrideModal.closeModal();
            changeLocationForOverrideModal.openModal();
          }}
        />
      </Modal>

      {settingsTenantLocation && (
        <Modal {...changeLocationForOverrideModal.modalProps} minWidth={580}>
          <ChangeLocationModal
            callRoute={callRoute}
            settingsTenantLocation={settingsTenantLocation}
            onClose={changeLocationForOverrideModal.modalProps.onClose}
            onSuccess={() => {
              changeLocationForOverrideModal.closeModal();
              editOverrideTray.openModal();
            }}
          />
        </Modal>
      )}
    </>
  );
};
